import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { motion } from "motion/react";
import { Lock, TriangleAlert, User } from "lucide-react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Alert, AlertTitle } from "@/components/ui/alert";
import { LoginFormFields, loginFormSchema } from "../schemes";

type LoginFormProps = {
	readonly error?: string | null;
	onLogin: (data: LoginFormFields) => void;
};

export function LoginForm({ onLogin, error }: LoginFormProps) {
	const { t } = useTranslation("auth");

	const form = useForm<LoginFormFields>({
		resolver: zodResolver(loginFormSchema),
		defaultValues: {
			username: "",
			password: "",
		},
	});

	return (
		<motion.div
			initial={{ opacity: 0, y: 14 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.45, ease: "easeOut" }}
		>
			<Card className="relative overflow-hidden border-border/60 bg-card/80 shadow-[0_20px_50px_-30px_rgba(15,23,42,0.5)] backdrop-blur">
				<div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.18),_transparent_55%)]" />
				<CardHeader>
					<CardTitle>{t("login.title")}</CardTitle>
					<CardDescription>{t("login.description")}</CardDescription>
				</CardHeader>

				<CardContent className="relative space-y-6 p-6">
					{error ? (
						<Alert>
							<TriangleAlert className="h-4 w-4" />
							<AlertTitle>{error}</AlertTitle>
						</Alert>
					) : null}

					<Form {...form}>
						<form className="flex flex-col space-y-5" onSubmit={form.handleSubmit(onLogin)}>
							<FormField
								control={form.control}
								name="username"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-[0.7rem] font-semibold uppercase tracking-[0.32em] text-muted-foreground">
											{t("login.fields.username.label")}
										</FormLabel>
										<div className="relative">
											<User className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
											<FormControl>
												<Input
													type="text"
													placeholder={t("login.fields.username.placeholder")}
													className="h-11 rounded-lg border-border/60 bg-background/60 pl-10 text-sm shadow-sm transition focus-visible:ring-2 focus-visible:ring-primary/40"
													{...field}
												/>
											</FormControl>
										</div>
										<FormMessage role="alert" />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="password"
								render={({ field }) => (
									<FormItem>
										<FormLabel className="text-[0.7rem] font-semibold uppercase tracking-[0.32em] text-muted-foreground">
											{t("login.fields.password.label")}
										</FormLabel>
										<div className="relative">
											<Lock className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
											<FormControl>
												<Input
													type="password"
													placeholder={t("login.fields.password.placeholder")}
													className="h-11 rounded-lg border-border/60 bg-background/60 pl-10 text-sm shadow-sm transition focus-visible:ring-2 focus-visible:ring-primary/40"
													{...field}
												/>
											</FormControl>
										</div>
										<FormMessage role="alert" />
									</FormItem>
								)}
							/>
							<Button
								type="submit"
								className="h-11 w-full rounded-lg text-sm font-semibold shadow-lg shadow-primary/20 transition hover:translate-y-[-1px] cursor-pointer"
								disabled={form.formState.isSubmitting}
							>
								{t("login.fields.buttons.login")}
							</Button>
						</form>
					</Form>
				</CardContent>
			</Card>
		</motion.div>
	);
}
